import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Brain,
  Cloud,
  Users,
  Shield,
  Zap,
  Target,
  TrendingUp,
  Award,
  Lightbulb,
  Globe,
  ChevronRight,
  Calendar,
  ArrowRight
} from 'lucide-react';
import SEO from '../components/SEO';
import QivratechLogo from '../components/QivratechLogo';

const AboutPage = () => {
  const [activeTimelineItem, setActiveTimelineItem] = useState(0);

  const coreValues = [
    {
      icon: <Brain className="w-12 h-12" />,
      title: "Innovation First",
      description: "We embrace cutting-edge technologies and innovative approaches to solve complex business challenges.",
      color: "from-cyan-400 to-blue-500"
    },
    {
      icon: <Users className="w-12 h-12" />,
      title: "Client-Centric",
      description: "Your success is our success. We build long-term partnerships based on trust and measurable results.",
      color: "from-purple-400 to-pink-500"
    },
    {
      icon: <Shield className="w-12 h-12" />,
      title: "Quality & Security",
      description: "We maintain the highest standards of code quality, security, and compliance in everything we deliver.",
      color: "from-green-400 to-teal-500"
    },
    {
      icon: <Zap className="w-12 h-12" />,
      title: "Agility & Speed",
      description: "We deliver rapid prototypes and iterative solutions that adapt quickly to changing business needs.",
      color: "from-yellow-400 to-orange-500"
    }
  ];

  const timeline = [
    {
      year: "2025",
      title: "Foundation & Vision",
      description: "Qivra Technologies was founded with a mission to democratize AI and make cutting-edge technology accessible to businesses of all sizes.",
      achievements: ["Founded by tech veterans", "First AI consulting projects", "Core team assembled"],
      milestone: "Company Founded"
    },
    {
      year: "2026",
      title: "Growth & Innovation",
      description: "Expanding our service offerings and building strong partnerships. Focus on delivering exceptional AI and IT solutions to our growing client base.",
      achievements: ["10+ projects delivered", "Cloud partnerships established", "Team expansion"],
      milestone: "Market Entry"
    }
  ];

  const stats = [
    { value: "10+", label: "Projects Delivered", icon: <Target className="w-6 h-6" /> },
    { value: "15+", label: "Happy Clients", icon: <Users className="w-6 h-6" /> },
    { value: "100%", label: "Client Satisfaction", icon: <Award className="w-6 h-6" /> },
    { value: "3", label: "Global Offices", icon: <Globe className="w-6 h-6" /> }
  ];

  const testimonials = [
    {
      name: "J",
      role: "CTO",
      image: "👨‍💼",
      quote: "Qivra Technologies revolutionized our real estate platform with cutting-edge technology. Their innovative solutions and dedicated team transformed how we do business.",
      rating: 5
    },
    {
      name: "George",
      role: "CEO",
      image: "👨‍💼",
      quote: "Qivra Technologies built our XRP-based crypto platform with exceptional expertise. Their blockchain knowledge and secure infrastructure exceeded all expectations.",
      rating: 5
    },
    {
      name: "Emily Rodriguez",
      role: "Product Manager",
      image: "👩‍💻",
      quote: "The custom software solution Qivra built for us streamlined our operations and increased efficiency by 40%. Highly recommended!",
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white font-sans">
      <SEO
        title="About Qivra Technologies - Our Vision, Mission & Journey"
        description="Learn about Qivra Technologies' vision, mission, core values, and growth journey. Intelligence unleashed for modern enterprises through cutting-edge AI and IT solutions."
        keywords="About Us, Qivra Technologies, Company Vision, Company Mission, Core Values, Company History, Digital Transformation, AI Company, Intelligence Unleashed"
      />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-950 to-black">
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `
                radial-gradient(circle at 25% 25%, rgba(34, 211, 238, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 75% 75%, rgba(168, 85, 247, 0.1) 0%, transparent 50%),
                radial-gradient(circle, rgba(14, 165, 233, 0.05) 1px, transparent 1px)
              `,
              backgroundSize: '100% 100%, 100% 100%, 60px 60px'
            }}
          />

          {/* Floating Logo Elements */}
          <div className="absolute inset-0 overflow-hidden">
            {/* Top Area */}
            <motion.div
              className="absolute top-20 left-10 opacity-6"
              animate={{
                y: [0, -25, 0],
                x: [0, 12, 0],
                rotate: [0, 4, 0]
              }}
              transition={{
                duration: 9,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <QivratechLogo variant="mark" size="large" theme="dark" />
            </motion.div>

            <motion.div
              className="absolute top-40 right-16 opacity-5"
              animate={{
                y: [0, -18, 0],
                x: [0, -8, 0],
                rotate: [0, -2, 0]
              }}
              transition={{
                duration: 13,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 3
              }}
            >
              <QivratechLogo variant="mark" size="default" theme="dark" />
            </motion.div>

            {/* Middle Area */}
            <motion.div
              className="absolute top-1/2 left-8 opacity-4"
              animate={{
                y: [0, -28, 0],
                x: [0, 18, 0],
                rotate: [0, 6, 0]
              }}
              transition={{
                duration: 16,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 4
              }}
            >
              <QivratechLogo variant="mark" size="default" theme="dark" />
            </motion.div>

            {/* Bottom Area */}
            <motion.div
              className="absolute bottom-32 left-16 opacity-7"
              animate={{
                y: [0, -22, 0],
                x: [0, 18, 0],
                rotate: [0, 6, 0]
              }}
              transition={{
                duration: 13,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 6
              }}
            >
              <QivratechLogo variant="mark" size="small" theme="dark" />
            </motion.div>

            <motion.div
              className="absolute bottom-20 right-12 opacity-8"
              animate={{
                y: [0, -32, 0],
                x: [0, -14, 0],
                rotate: [0, -5, 0]
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 8
              }}
            >
              <QivratechLogo variant="mark" size="large" theme="dark" />
            </motion.div>
          </div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500/20 to-purple-600/20 px-4 py-2 rounded-full border border-cyan-400/30 mb-8">
              <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></span>
              <span className="text-cyan-400 text-sm font-light tracking-wide">Our Story & Vision</span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-extralight mb-8 leading-tight tracking-wide">
              <span className="text-white">About</span>
              <br />
              <span className="bg-gradient-to-r from-cyan-300 via-blue-400 to-purple-500 bg-clip-text text-transparent font-light">
                Qivratech
              </span>
            </h1>

            <p className="text-2xl text-gray-300 max-w-4xl mx-auto font-light leading-relaxed tracking-wide">
              Intelligence unleashed through passionate technologists, researchers, and strategists transforming businesses with cutting-edge solutions
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-cyan-500/10 to-blue-600/10 p-8 rounded-3xl border border-cyan-400/30"
            >
              <div className="flex items-center gap-4 mb-6">
                <Lightbulb className="w-10 h-10 text-cyan-400" />
                <h2 className="text-3xl font-light text-cyan-400">Our Vision</h2>
              </div>
              <p className="text-lg text-gray-400 leading-relaxed font-light">
                Empowering businesses with intelligent technology solutions for sustainable growth and innovation.
              </p>

            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-purple-600/10 to-pink-600/10 p-8 rounded-3xl border border-purple-400/30"
            >
              <div className="flex items-center gap-4 mb-6">
                <Target className="w-10 h-10 text-purple-400" />
                <h2 className="text-3xl font-light text-purple-400">Our Mission</h2>
              </div>
              <p className="text-lg text-gray-400 leading-relaxed font-light">
                Delivering innovative AI solutions that drive measurable results and seamless digital transformation.
              </p>

            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-light mb-6">
              <span className="bg-gradient-to-r from-cyan-400 to-purple-600 bg-clip-text text-transparent">
                Our Core Values
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              The principles that guide our decisions, shape our culture, and drive our commitment to excellence.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {coreValues.map((value, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.2 }}
                viewport={{ once: true }}
                whileHover={{ y: -10, scale: 1.02 }}
                className="bg-gradient-to-br from-gray-900 to-black p-8 rounded-3xl border border-gray-800 hover:border-cyan-400/50 transition-all text-center group"
              >
                <div className={`text-cyan-400 mb-6 group-hover:scale-110 transition-transform`}>
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-white mb-4 group-hover:text-cyan-400 transition-colors">
                  {value.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Timeline */}
      <section className="py-24 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-light mb-6">
              <span className="bg-gradient-to-r from-cyan-400 to-purple-600 bg-clip-text text-transparent">
                Our Journey
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              From startup to industry leader - the milestones that shaped our growth and success.
            </p>
          </motion.div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-cyan-400 to-purple-600 opacity-30"></div>

            <div className="space-y-12">
              {timeline.map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: idx % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: idx * 0.2 }}
                  viewport={{ once: true }}
                  className={`flex items-center ${idx % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
                  onMouseEnter={() => setActiveTimelineItem(idx)}
                >
                  <div className={`w-5/12 ${idx % 2 === 0 ? 'text-right pr-8' : 'text-left pl-8'}`}>
                    <div className="bg-gradient-to-br from-gray-900 to-black p-6 rounded-2xl border border-gray-800 hover:border-cyan-400/50 transition-all">
                      <div className="text-3xl font-black text-cyan-400 mb-2">{item.year}</div>
                      <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                      <p className="text-gray-300 mb-4">{item.description}</p>
                      <div className="space-y-2">
                        {item.achievements.map((achievement, achIdx) => (
                          <div key={achIdx} className="flex items-center gap-2 text-sm text-gray-400">
                            <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full"></div>
                            <span>{achievement}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="relative z-10 w-2/12 flex justify-center">
                    <div className={`w-6 h-6 rounded-full border-4 ${activeTimelineItem === idx
                      ? 'bg-cyan-400 border-cyan-400 scale-125'
                      : 'bg-gray-900 border-gray-600'
                      } transition-all duration-300`}></div>
                  </div>

                  <div className="w-5/12"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* Testimonials Section */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-light mb-6">
              <span className="bg-gradient-to-r from-cyan-400 to-purple-600 bg-clip-text text-transparent">
                What Our Clients Say
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Don't just take our word for it - hear from the businesses we've helped transform.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 items-stretch">
            {testimonials.map((testimonial, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.2 }}
                viewport={{ once: true }}
                whileHover={{ y: -10, scale: 1.02 }}
                className="h-full flex flex-col bg-gradient-to-br from-gray-900 to-black p-8 rounded-3xl border border-gray-800 hover:border-cyan-400/50 transition-all"
              >
                {/* Rating Stars */}
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-yellow-400 text-xl">★</span>
                  ))}
                </div>

                {/* Quote */}
                <p className="text-gray-300 mb-6 leading-relaxed italic">
                  "{testimonial.quote}"
                </p>

                {/* Author Info */}
                <div className="flex items-center gap-4 pt-6 border-t border-gray-800">
                  <div className="text-4xl">{testimonial.image}</div>
                  <div>
                    <h4 className="text-white font-bold">{testimonial.name}</h4>
                    <p className="text-gray-400 text-sm">{testimonial.role}</p>
                    <p className="text-cyan-400 text-sm">{testimonial.company}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-cyan-500/10 to-purple-600/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-light text-white mb-6">
              Join Our Journey
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Ready to be part of the future? Explore opportunities to work with us or partner with our team.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="/careers"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-cyan-400 to-purple-600 px-8 py-4 rounded-full font-bold text-white hover:shadow-xl hover:shadow-cyan-400/50 transition-all flex items-center gap-2 justify-center"
              >
                Join Our Team
                <ChevronRight className="w-5 h-5" />
              </motion.a>
              <motion.a
                href="/contact"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-cyan-400 text-cyan-400 px-8 py-4 rounded-full font-bold hover:bg-cyan-400 hover:text-black transition-all"
              >
                Partner With Us
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;